import { configService } from "@/services/config-service";
import { UserPayloadInterface } from "@/services/onboarding/model/userPayload";
import { SetCredentialsPayloadInterface } from "@/services/onboarding/model/setCredentials";
import {
  APPLICATION_JSON_HEADERS,
  HTTP_METHOD,
  withJwt,
} from "@/services/http/httpUtils";
import { HttpBadRequestError } from "@/services/http/httpErrors";

export enum OnboardingStatus {
  emailVerificationPending = "emailVerificationPending",
  emailVerified = "emailVerified",
  accountProvisioned = "accountProvisioned",
}

export interface UserResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: OnboardingStatus;
}

export interface FinalizeReponse {
  sessionToken: string;
}

export interface ActivationResponse {
  resendActiveAt: string;
}

export interface RecoveryQuestion {
  questionText: string;
}

class OnboardingService {
  private get API_URL(): string {
    return configService.apiUrl;
  }

  async submitProspect(user: UserPayloadInterface): Promise<Response> {
    return fetch(`${this.API_URL}/internal/register`, {
      method: HTTP_METHOD.POST,
      headers: APPLICATION_JSON_HEADERS,
      body: JSON.stringify(user),
    });
  }

  async submitCredentials(
    credentialsPayload: SetCredentialsPayloadInterface,
    jwt: string
  ): Promise<FinalizeReponse | void> {
    return fetch(`${this.API_URL}/internal/sign-up/finalize`, {
      method: HTTP_METHOD.POST,
      headers: withJwt(jwt),
      body: JSON.stringify(credentialsPayload),
    }).then((response) => {
      if (response.status === 200) {
        return response.json();
      }

      switch (response.status) {
        case 422:
        case 404:
        case 410:
          throw new HttpBadRequestError(response);
        default:
          throw new Error("Something went wrong during set credentials");
      }
    });
  }

  async status(jwt: string): Promise<UserResponse> {
    return fetch(`${this.API_URL}/internal/status`, {
      method: HTTP_METHOD.GET,
      headers: withJwt(jwt),
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }

      return Promise.reject(response.status);
    });
  }

  async verifyEmail(jwt: string) {
    return fetch(`${this.API_URL}/internal/verify-email`, {
      method: HTTP_METHOD.POST,
      headers: withJwt(jwt),
    }).then((response) => {
      if (!response.ok) {
        throw new Error("Something went wrong during email verification");
      }
    });
  }

  async getRecoveryQuestions(jwt: string): Promise<RecoveryQuestion> {
    return fetch(`${this.API_URL}/internal/sign-up/recovery-questions`, {
      method: HTTP_METHOD.GET,
      headers: withJwt(jwt),
    }).then((response) => response.json());
  }

  async resend(): Promise<ActivationResponse> {
    return new Promise((resolve) =>
      resolve({
        resendActiveAt: new Date(
          new Date().getTime() + 1000 * 60
        ).toISOString(),
      })
    );
  }
}

export const onboardingService = new OnboardingService();
